// AI features service — wraps the /ai endpoints per the backend contract.
// Uses the same auth-header pattern as api.js (getHeaders), but keeps its own
// request path because the pages need structured access to the contract's
// error codes (limit_reached / premium_required / no_recipe) alongside the
// standard toast-on-failure behaviour from error-surfacing.md.
import toast from 'react-hot-toast';
import { getHeaders } from './api';

const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT;
const TRANSIENT_MESSAGE = 'Could not process that — please try again.';

// Error codes the calling page handles itself (upgrade prompt / inline copy) —
// no toast for these, the page renders the state.
const PAGE_HANDLED_CODES = ['limit_reached', 'premium_required', 'no_recipe'];

export class AiError extends Error {
  constructor(message, { status = 0, code = 'unknown', usage = null } = {}) {
    super(message);
    this.name = 'AiError';
    this.status = status;
    this.code = code;
    this.usage = usage;
  }
}

async function aiRequest(path, body) {
  if (!API_ENDPOINT) {
    toast.error(TRANSIENT_MESSAGE);
    throw new AiError('API not configured', { code: 'not_configured' });
  }

  const headers = await getHeaders();
  let res;
  try {
    res = await fetch(`${API_ENDPOINT}${path}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });
  } catch {
    toast.error(TRANSIENT_MESSAGE);
    throw new AiError('Network error', { code: 'transient' });
  }

  if (res.ok) return res.json();

  if (res.status === 401) {
    window.location.href = '/auth/login';
    throw new AiError('Not signed in', { status: 401, code: 'unauthenticated' });
  }

  const payload = await res.json().catch(() => ({}));

  // 502/504 (and any other 5xx) — transient AI failure per the contract.
  if (res.status >= 500) {
    toast.error(TRANSIENT_MESSAGE);
    throw new AiError(payload.message || TRANSIENT_MESSAGE, { status: res.status, code: 'transient' });
  }

  const code = payload.error || 'unknown';
  const message = payload.message || `Request failed (${res.status})`;
  if (!PAGE_HANDLED_CODES.includes(code)) toast.error(message);
  throw new AiError(message, { status: res.status, code, usage: payload.usage || null });
}

export const aiApi = {
  // source: { type: 'image', mediaType, data } | { type: 'text', text }
  extractRecipe: (source) => aiRequest('/ai/extract-recipe', { source }),
  // recipe: { title, ingredients, steps, servings } in contract shape; ageMonths: 6–36
  adaptForBaby: (recipe, ageMonths) => aiRequest('/ai/adapt-baby', { recipe, ageMonths }),
};

// ---------------------------------------------------------------------------
// Shape mappers between the AI contract and the local recipe model.
// Local recipes use { qty, unit, item, group } ingredients and
// { order, instruction, timerMinutes } steps; the AI contract uses
// { quantity, unit, name } ingredients and plain-string steps.
// ---------------------------------------------------------------------------

function parseQuantity(value) {
  if (typeof value === 'number') return value;
  if (!value) return 0;
  const str = String(value).trim();
  const mixed = str.match(/^(\d+)\s+(\d+)\/(\d+)$/);
  if (mixed) return parseInt(mixed[1], 10) + parseInt(mixed[2], 10) / parseInt(mixed[3], 10);
  const frac = str.match(/^(\d+)\/(\d+)$/);
  if (frac) return parseInt(frac[1], 10) / parseInt(frac[2], 10);
  const n = parseFloat(str);
  return Number.isNaN(n) ? 0 : n;
}

// Extracted recipe → prefill shape for RecipeEditorPage (/submit).
export function extractedToRecipeForm(recipe = {}) {
  const ingredients = (recipe.ingredients || []).map((ing) => {
    const qty = parseQuantity(ing.quantity);
    return { qty: qty ? String(qty) : '', unit: ing.unit || '', item: ing.name || '', group: 'Main' };
  });
  const steps = (recipe.steps || []).map((instruction, i) => ({
    order: i + 1,
    instruction: typeof instruction === 'string' ? instruction : '',
    timerMinutes: '',
  }));
  const prefill = {
    title: recipe.title || '',
    description: recipe.description || '',
    servings: recipe.servings || 4,
    prepTime: recipe.prepTime || '',
    cookTime: recipe.cookTime || '',
    categories: recipe.category ? [recipe.category] : [],
    tags: Array.isArray(recipe.tags) ? recipe.tags : [],
    ingredients: ingredients.length ? ingredients : [{ qty: '', unit: '', item: '', group: 'Main' }],
    steps: steps.length ? steps : [{ order: 1, instruction: '', timerMinutes: '' }],
  };
  if (recipe.audience) prefill.audience = recipe.audience;
  return prefill;
}

// Local recipe → the adapt-baby request body's recipe shape.
export function recipeToAiPayload(recipe = {}) {
  return {
    title: recipe.title || '',
    servings: recipe.servings || 0,
    ingredients: (recipe.ingredients || []).map((ing) => ({
      quantity: ing.qty ?? '',
      unit: ing.unit || '',
      name: ing.item || '',
    })),
    steps: (recipe.steps || []).map((s) => (typeof s === 'string' ? s : s.instruction || '')),
  };
}

// Adaptation response → a new local recipe ready for addRecipe().
export function adaptationToRecipe(adaptation = {}, baseRecipe = {}) {
  const suitableFromMonths = adaptation.suitableFromMonths;
  const audience = typeof suitableFromMonths === 'number' && suitableFromMonths < 12 ? 'baby' : 'toddler';
  return {
    title: adaptation.title || `${baseRecipe.title || 'Recipe'} (baby & toddler)`,
    description: adaptation.intro || '',
    servings: baseRecipe.servings || 2,
    prepTime: baseRecipe.prepTime || 0,
    cookTime: baseRecipe.cookTime || 0,
    totalTime: baseRecipe.totalTime || 0,
    categories: ['Baby Food'],
    tags: [...new Set([...(baseRecipe.tags || []), 'baby-adapted'])],
    audience,
    ageGroups: [audience],
    suitableFromMonths: suitableFromMonths ?? null,
    ingredients: (adaptation.ingredients || []).map((ing) => ({
      qty: parseQuantity(ing.quantity),
      unit: ing.unit || '',
      item: ing.name || '',
      group: 'Main',
    })),
    steps: (adaptation.steps || []).map((instruction, i) => ({
      order: i + 1,
      instruction: typeof instruction === 'string' ? instruction : '',
      timerMinutes: null,
    })),
    notes: (adaptation.safetyNotes || []).join('\n'),
    sourceUrl: baseRecipe.sourceUrl || '',
    favorite: false,
  };
}
